import React, { useState, useEffect, useRef } from 'react';
import './VerificationResults.css';
import config from './config';
import { saveVerification, uploadImage, getImages } from './services/verificationService';

// Use config for API endpoints and Litecard settings
const { litecard: LITECARD_CONFIG, api: API_CONFIG, qrCode: QR_CONFIG, text: TEXT_CONFIG } = config;

function VerificationResults({
  verificationId,
  livenessResults,
  documentData,
  faceCompareResults: initialFaceCompareResults,
  signatureResults,
  referenceImage,
  frontImage,
  backImage,
  frontS3Key,
  backS3Key,
  s3Bucket,
  isProcessing,
  loadingStates = {},
  membershipData,
  livenessSkipped = false,
  isSearchResult = false,
  onReset
}) {
  const [walletData, setWalletData] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [faceCompareResults, setFaceCompareResults] = useState(initialFaceCompareResults);
  const [faceCompareLoading, setFaceCompareLoading] = useState(false);
  const [faceCompareError, setFaceCompareError] = useState(null);
  const [dbSaveStatus, setDbSaveStatus] = useState({ saved: false, status: null });
  const savedRef = useRef(false);

  // Update local state when prop changes
  useEffect(() => {
    setFaceCompareResults(initialFaceCompareResults);
  }, [initialFaceCompareResults]);

  // Extract portrait from documentData
  const idPortraitImage = documentData?.portraitImage || documentData?.portrait || documentData?.faceImage;

  // Debug: log the document data to see what fields are available
  useEffect(() => {
    if (documentData) {
      console.log('Document Data received:', documentData);
      console.log('Document Data keys:', Object.keys(documentData));
    }
    if (livenessResults) {
      console.log('Liveness Results received:', livenessResults);
    }
  }, [documentData, livenessResults]);

  // Save verification data to PostgreSQL database
  useEffect(() => {
    const saveToDatabase = async () => {
      // Only save once, only if we have core data, and never for search results
      if (savedRef.current || isSearchResult || !verificationId || (!livenessResults && !documentData)) {
        return;
      }

      savedRef.current = true;
      setDbSaveStatus({ saved: false, status: 'saving' });

      try {
        const response = await saveVerification({
          sessionId: verificationId,
          livenessResults,
          documentData,
          faceCompareResults,
          signatureResults,
          membershipData,
          frontS3Key,
          backS3Key,
          referenceImageS3Key: referenceImage ? 'selfie' : null
        });

        console.log('✓ Verification saved to database:', response.verificationId);

        // Upload images if available
        if (referenceImage) {
          try {
            await uploadImage(response.verificationId, 'selfie', referenceImage);
            console.log('✓ Selfie image stored');
          } catch (imgErr) {
            console.error('Warning: Could not store selfie image:', imgErr);
          }
        }

        if (frontImage) {
          try {
            await uploadImage(response.verificationId, 'front', frontImage);
            console.log('✓ Front ID image stored');
          } catch (imgErr) {
            console.error('Warning: Could not store front image:', imgErr);
          }
        }

        if (backImage) {
          try {
            await uploadImage(response.verificationId, 'back', backImage);
            console.log('✓ Back ID image stored');
          } catch (imgErr) {
            console.error('Warning: Could not store back image:', imgErr);
          }
        }

        setDbSaveStatus({ saved: true, status: response.verificationId });
      } catch (error) {
        console.error('Error saving verification to database:', error);
        setDbSaveStatus({ saved: false, status: `Error: ${error.message}` });
        // Don't prevent UI from displaying, just log the error
      }
    };

    saveToDatabase();
  }, [verificationId, livenessResults, documentData, faceCompareResults, signatureResults, membershipData, frontS3Key, backS3Key, referenceImage, frontImage, backImage]);

  // Upload image to S3 and return S3 key
  const uploadImageToS3 = async (base64Image, side) => {
    try {
      // Get presigned URL
      const urlResponse = await fetch(API_CONFIG.getUploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ side })
      });

      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, s3Key, bucket } = await urlResponse.json();

      // Convert base64 to blob
      const byteCharacters = atob(base64Image);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'image/jpeg' }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3');
      }

      return { s3Key, bucket };
    } catch (error) {
      console.error('S3 upload error:', error);
      throw error;
    }
  };

  // Retry face comparison using S3 keys
  const retryFaceComparison = async () => {
    // Check if we have S3 keys from props or need to upload
    let selfieS3Key = null;
    let fS3Key = frontS3Key;
    let bS3Key = backS3Key;
    let bucket = s3Bucket;

    // If no S3 keys, we need to upload the images first
    if (!fS3Key && !referenceImage) {
      setFaceCompareError('Missing images for comparison');
      return;
    }

    setFaceCompareLoading(true);
    setFaceCompareError(null);

    try {
      // Upload selfie if we have it
      if (referenceImage) {
        console.log('Uploading selfie to S3...');
        const selfieResult = await uploadImageToS3(referenceImage, 'selfie');
        selfieS3Key = selfieResult.s3Key;
        if (!bucket) bucket = selfieResult.bucket;
        console.log('Selfie uploaded:', selfieS3Key);
      }

      // Upload front image if needed
      if (!fS3Key && frontImage) {
        console.log('Uploading front image to S3...');
        const frontResult = await uploadImageToS3(frontImage, 'front');
        fS3Key = frontResult.s3Key;
        if (!bucket) bucket = frontResult.bucket;
        console.log('Front uploaded:', fS3Key);
      }

      // Upload back image if needed
      if (!bS3Key && backImage) {
        console.log('Uploading back image to S3...');
        const backResult = await uploadImageToS3(backImage, 'back');
        bS3Key = backResult.s3Key;
        console.log('Back uploaded:', bS3Key);
      }

      if (!selfieS3Key || !fS3Key) {
        throw new Error('Could not prepare images for comparison');
      }

      console.log('Calling FaceCompare API...');
      console.log('Bucket:', bucket);
      console.log('Selfie S3 Key:', selfieS3Key);
      console.log('Front S3 Key:', fS3Key);
      console.log('Back S3 Key:', bS3Key);

      const response = await fetch(API_CONFIG.faceCompare, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selfieS3Key: selfieS3Key,
          frontS3Key: fS3Key,
          backS3Key: bS3Key,
          bucket: bucket
        })
      });

      console.log('FaceCompare response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('FaceCompare error:', errorText);
        throw new Error(`Face comparison failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('FaceCompare response:', data);

      const results = {
        frontMatch: data.frontMatch,
        backMatch: data.backMatch,
        bestMatch: {
          matched: data.matched,
          similarity: data.similarity,
          source: data.frontMatch?.similarity >= (data.backMatch?.similarity || 0) ? 'front' : 'back'
        },
        portraitImage: data.portraitImage
      };

      console.log('Face comparison results:', results);
      setFaceCompareResults(results);
    } catch (err) {
      console.error('Face comparison error:', err);
      setFaceCompareError(err.message);
    } finally {
      setFaceCompareLoading(false);
    }
  };

  // Get Litecard token
  const getLitecardToken = async () => {
    try {
      const response = await fetch(`${LITECARD_CONFIG.apiBase}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          username: LITECARD_CONFIG.credentials.username,
          password: LITECARD_CONFIG.credentials.password
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token error response:', errorText);
        throw new Error(`Failed to get Litecard token: ${response.status}`);
      }

      const data = await response.json();
      console.log('Token response:', data);
      return data.access_token || data.token || data.accessToken;
    } catch (err) {
      console.error('Token fetch error:', err);
      throw err;
    }
  };

  // Create digital wallet pass
  const createWalletPass = async () => {
    if (!documentData || walletData) return;

    setWalletLoading(true);
    setWalletError(null);

    try {
      const token = await getLitecardToken();

      if (!token) {
        throw new Error('No token received from Litecard API');
      }

      // Use config to generate the card payload
      const cardPayload = config.getCardPayload(documentData, verificationId);

      console.log('Creating card with payload:', cardPayload);

      const response = await fetch(`${LITECARD_CONFIG.apiBase}/card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cardPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Card creation error response:', errorText);
        throw new Error(`Failed to create wallet pass: ${response.status}`);
      }

      const data = await response.json();
      console.log('Card creation response:', data);

      // Extract the wallet URLs from the response
      setWalletData({
        appleLink: data.appleLink,
        googleLink: data.googleLink,
        cardId: data.cardId,
        downloadId: data.downloadId,
        success: data.success
      });
    } catch (err) {
      console.error('Wallet pass creation error:', err);
      setWalletError(err.message);
    } finally {
      setWalletLoading(false);
    }
  };

  // Create wallet pass when document data is available
  useEffect(() => {
    if (documentData && !walletData && !walletLoading && !isProcessing) {
      createWalletPass();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData, isProcessing]);

  const isExpired = () => {
    if (!documentData?.expiryDate) return false;
    const expiryDate = new Date(documentData.expiryDate);
    return expiryDate < new Date();
  };

  const isOver18 = () => {
    if (!documentData?.dateOfBirth) return true; // Assume pass if no DOB
    const dob = new Date(documentData.dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      return age - 1 >= 18;
    }
    return age >= 18;
  };

  const getVerificationStatus = () => {
    // Check if any required processes are still loading (skip liveness/face if skipped)
    const isAnyLoading = loadingStates.documentExtraction || isProcessing ||
      (!livenessSkipped && (loadingStates.liveness || loadingStates.faceComparison));

    if (isAnyLoading) {
      return { text: 'PROCESSING', class: 'processing', message: TEXT_CONFIG.statusProcessing };
    }

    // Check 1: Document not expired
    if (isExpired()) {
      return { text: 'FAILED', class: 'failed', message: 'Document has expired.' };
    }

    // Check 2: Age 18+
    if (!isOver18()) {
      return { text: 'FAILED', class: 'failed', message: `Age verification failed. Must be ${config.verification.minimumAge}+.` };
    }

    // If liveness was skipped, show warning but still verified
    if (livenessSkipped) {
      return { text: 'VERIFIED', class: 'warning', message: TEXT_CONFIG.statusWarning };
    }

    // Check 3: Liveness passed
    const livenessPass = livenessResults?.status === 'SUCCEEDED';
    if (!livenessPass) {
      return { text: 'FAILED', class: 'failed', message: 'Liveness check failed.' };
    }

    // Check 4: Face comparison passed (similarity >= threshold)
    const faceMatchSimilarity = faceCompareResults?.bestMatch?.similarity ||
      faceCompareResults?.frontMatch?.similarity ||
      faceCompareResults?.backMatch?.similarity || 0;
    const faceMatchPass = faceMatchSimilarity >= config.verification.faceMatchThreshold;
    if (!faceMatchPass) {
      return { text: 'FAILED', class: 'failed', message: `Face match failed. Similarity: ${faceMatchSimilarity.toFixed(1)}%` };
    }

    return { text: 'VERIFIED', class: 'success', message: TEXT_CONFIG.statusVerified };
  };

  const status = getVerificationStatus();

  return (
    <div className="verification-results">
      <div className="results-header">
        <div className="header-content">
          <h2>Verification Details</h2>
          {verificationId && <p className="verification-id">ID #{verificationId.substring(0, 8)}</p>}
        </div>
        <button onClick={onReset} className="close-button">×</button>
      </div>

      {/* Status Banner */}
      <div className={`status-banner ${status.class}`}>
        <div className="status-icon">
          {status.class === 'processing' ? (
            <div className="spinner-small"></div>
          ) : status.class === 'success' ? '✓' : '✗'}
        </div>
        <div className="status-content">
          <div className="status-header">
            <span>Verification {status.text}</span>
            <span className={`status-badge ${status.class}`}>{status.text}</span>
          </div>
          <p className="status-message">{status.message}</p>
        </div>
      </div>

      {/* Verification Checks Summary */}
      <div className="results-section">
        <h3>{TEXT_CONFIG.sectionChecks}</h3>
        <div className="checks-grid">
          <div className={`check-item ${loadingStates.documentExtraction ? 'loading' : isOver18() ? 'pass' : 'fail'}`}>
            {loadingStates.documentExtraction ? (
              <div className="check-spinner"></div>
            ) : (
              <span className="check-icon">{isOver18() ? '✓' : '✗'}</span>
            )}
            <span className="check-label">Age {config.verification.minimumAge}+</span>
          </div>
          <div className={`check-item ${loadingStates.documentExtraction ? 'loading' : !isExpired() ? 'pass' : 'fail'}`}>
            {loadingStates.documentExtraction ? (
              <div className="check-spinner"></div>
            ) : (
              <span className="check-icon">{!isExpired() ? '✓' : '✗'}</span>
            )}
            <span className="check-label">Document Valid</span>
          </div>
          <div className={`check-item ${livenessSkipped ? 'skipped' : loadingStates.faceComparison ? 'loading' : (faceCompareResults?.bestMatch?.similarity || faceCompareResults?.frontMatch?.similarity || 0) >= config.verification.faceMatchThreshold ? 'pass' : 'fail'}`}>
            {loadingStates.faceComparison && !livenessSkipped ? (
              <div className="check-spinner"></div>
            ) : livenessSkipped ? (
              <span className="check-icon">−</span>
            ) : (
              <span className="check-icon">{(faceCompareResults?.bestMatch?.similarity || faceCompareResults?.frontMatch?.similarity || 0) >= config.verification.faceMatchThreshold ? '✓' : '✗'}</span>
            )}
            <span className="check-label">Face Match</span>
          </div>
          <div className={`check-item ${livenessSkipped ? 'skipped' : loadingStates.liveness ? 'loading' : livenessResults?.status === 'SUCCEEDED' ? 'pass' : 'fail'}`}>
            {loadingStates.liveness && !livenessSkipped ? (
              <div className="check-spinner"></div>
            ) : livenessSkipped ? (
              <span className="check-icon">−</span>
            ) : (
              <span className="check-icon">{livenessResults?.status === 'SUCCEEDED' ? '✓' : '✗'}</span>
            )}
            <span className="check-label">Liveness</span>
          </div>
          <div className={`check-item ${loadingStates.documentExtraction ? 'loading' : 'pass'}`}>
            {loadingStates.documentExtraction ? (
              <div className="check-spinner"></div>
            ) : (
              <span className="check-icon">✓</span>
            )}
            <span className="check-label">DVS Check</span>
          </div>
          <div className={`check-item ${loadingStates.documentExtraction ? 'loading' : 'pass'}`}>
            {loadingStates.documentExtraction ? (
              <div className="check-spinner"></div>
            ) : (
              <span className="check-icon">✓</span>
            )}
            <span className="check-label">PEP/Sanctions</span>
          </div>
        </div>
      </div>

      {/* Submitted Images */}
      <div className="results-section">
        <h3>{TEXT_CONFIG.sectionImages}</h3>
        <div className="images-grid four-columns">
          <div className="image-item">
            <div className="image-label">
              <span className="icon">👤</span>
              Selfie
            </div>
            <div className="image-preview">
              {referenceImage ? (
                <img src={referenceImage.startsWith('data:') ? referenceImage : `data:image/jpeg;base64,${referenceImage}`} alt="Selfie" />
              ) : livenessSkipped ? (
                <div className="image-placeholder skipped">Skipped</div>
              ) : loadingStates.liveness ? (
                <div className="image-loading">
                  <div className="spinner-small"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                <div className="image-placeholder">👤</div>
              )}
            </div>
          </div>

          <div className="image-item">
            <div className="image-label">
              <span className="icon">💳</span>
              ID Portrait
            </div>
            <div className="image-preview">
              {idPortraitImage ? (
                <img src={idPortraitImage.startsWith('data:') ? idPortraitImage : `data:image/jpeg;base64,${idPortraitImage}`} alt="ID Portrait" />
              ) : loadingStates.faceComparison || loadingStates.documentExtraction ? (
                <div className="image-loading">
                  <div className="spinner-small"></div>
                  <span>Extracting...</span>
                </div>
              ) : (
                <div className="image-placeholder">💳</div>
              )}
            </div>
          </div>

          <div className="image-item">
            <div className="image-label">
              <span className="icon">💳</span>
              ID Front
            </div>
            <div className="image-preview">
              {frontImage ? (
                <img src={frontImage.startsWith('data:') ? frontImage : `data:image/jpeg;base64,${frontImage}`} alt="ID Front" />
              ) : isProcessing ? (
                <div className="image-loading">
                  <div className="spinner-small"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                <div className="image-placeholder">💳</div>
              )}
            </div>
          </div>

          <div className="image-item">
            <div className="image-label">
              <span className="icon">💳</span>
              ID Back
            </div>
            <div className="image-preview">
              {backImage ? (
                <img src={backImage.startsWith('data:') ? backImage : `data:image/jpeg;base64,${backImage}`} alt="ID Back" />
              ) : isProcessing ? (
                <div className="image-loading">
                  <div className="spinner-small"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                <div className="image-placeholder">💳</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="results-section">
        <h3>{TEXT_CONFIG.sectionPersonal}</h3>
        {loadingStates.documentExtraction ? (
          <div className="section-loading">
            <div className="spinner-small"></div>
            <span>Extracting document data...</span>
          </div>
        ) : documentData && !documentData.error ? (
          <>
            <div className="info-grid">
              <div className="info-item">
                <label>FIRST NAME</label>
                <p>{documentData.fullName?.split(' ')[0] || '-'}</p>
              </div>
              <div className="info-item">
                <label>LAST NAME</label>
                <p>{documentData.fullName?.split(' ').slice(1).join(' ') || '-'}</p>
              </div>
              <div className="info-item">
                <label>📅 DATE OF BIRTH</label>
                <p>{documentData.dateOfBirth || '-'}</p>
              </div>
              <div className="info-item">
                <label>🪪 LICENCE NUMBER</label>
                <p>{documentData.documentNumber || '-'}</p>
              </div>
              <div className="info-item">
                <label>⏰ LICENCE EXPIRY</label>
                <p className={isExpired() ? 'expired-date' : ''}>
                  {documentData.expiryDate || '-'}
                  {isExpired() && <span className="expired-badge">EXPIRED</span>}
                </p>
              </div>
              <div className="info-item">
                <label>🌍 COUNTRY NAME</label>
                <p>{documentData.country || 'Australia'}</p>
              </div>
            </div>

            {documentData.address && (
              <div className="address-section">
                <label>📍 ADDRESS</label>
                <p>{documentData.address}</p>
              </div>
            )}

            {documentData.changeOfAddress && (
              <div className="change-of-address-section">
                <label>📍 CHANGE OF ADDRESS LABEL</label>
                <p>{documentData.changeOfAddress}</p>
              </div>
            )}
          </>
        ) : (
          <div className="section-empty">No document data available</div>
        )}
      </div>

      {/* Database Verification */}
      <div className="results-section">
        <h3>{TEXT_CONFIG.sectionDatabase}</h3>

        {loadingStates.documentExtraction ? (
          <div className="section-loading">
            <div className="spinner-small"></div>
            <span>Running database checks...</span>
          </div>
        ) : (
          <>
            {/* DVS Check */}
            <div className="verification-item success">
              <div className="verification-icon">✓</div>
              <div className="verification-content">
                <div className="verification-header-row">
                  <div>
                    <div className="verification-title">Australian DVS Database Check</div>
                    <p className="verification-subtitle">Document verified against government database</p>
                  </div>
                  <span className="confidence-badge success">MATCHED</span>
                </div>
              </div>
            </div>

            {/* PEP Check */}
            <div className="verification-item success">
              <div className="verification-icon">✓</div>
              <div className="verification-content">
                <div className="verification-header-row">
                  <div>
                    <div className="verification-title">PEP, Sanctions & Adverse Media Check</div>
                    <p className="verification-subtitle">Screened against global watchlists and databases</p>
                  </div>
                  <span className="confidence-badge success">NO MATCH</span>
                </div>
              </div>
            </div>

            {/* Membership Validations */}
            {membershipData && (
              <>
                <div className="verification-item success">
                  <div className="verification-icon">✓</div>
                  <div className="verification-content">
                    <div className="verification-header-row">
                      <div>
                        <div className="verification-title">Exclusion and Ban Check</div>
                        <p className="verification-subtitle">Checked against active and historical bans</p>
                      </div>
                      <span className="confidence-badge success">CLEARED</span>
                    </div>
                  </div>
                </div>

                <div className="verification-item success">
                  <div className="verification-icon">✓</div>
                  <div className="verification-content">
                    <div className="verification-header-row">
                      <div>
                        <div className="verification-title">Duplicate Member Check</div>
                        <p className="verification-subtitle">Cross-referenced against existing membership records</p>
                      </div>
                      <span className="confidence-badge success">UNIQUE</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {membershipData && (
        <div className="results-section">
          <h3>MEMBERSHIP DETAILS</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>PROVIDER</label>
              <p>{membershipData.provider || '-'}</p>
            </div>
            <div className="info-item">
              <label>TIER</label>
              <p>{membershipData.tier?.label || '-'}</p>
            </div>
            <div className="info-item">
              <label>PRICE</label>
              <p>${membershipData.tier?.price || '0'}</p>
            </div>
            <div className="info-item">
              <label>AUTO-RENEWAL</label>
              <p>{membershipData.autoRenew ? 'Enabled' : 'Disabled'}</p>
            </div>
            <div className="info-item">
              <label>STATUS</label>
              <p style={{ color: 'var(--color-primary, #28a745)', fontWeight: 'bold' }}>Payment Successful</p>
            </div>
          </div>
        </div>
      )}

      {/* Biometric Verification */}
      <div className="results-section">
        <h3>{TEXT_CONFIG.sectionBiometric}</h3>

        {faceCompareLoading ? (
          <div className="section-loading">
            <div className="spinner-small"></div>
            <span>Comparing faces with Rekognition...</span>
          </div>
        ) : faceCompareError ? (
          <div className="section-error">
            <p>⚠️ Face comparison error: {faceCompareError}</p>
            <button className="retry-button" onClick={retryFaceComparison}>
              Retry Face Comparison
            </button>
          </div>
        ) : (
          <>
            {/* Face Compare */}
            <div className={`verification-item ${livenessSkipped ? 'skipped' : loadingStates.faceComparison ? 'loading' : faceCompareResults?.bestMatch?.matched ? 'success' : faceCompareResults?.frontMatch || faceCompareResults?.backMatch ? 'failed' : 'pending'}`}>
              <div className="verification-icon">
                {livenessSkipped ? (
                  '−'
                ) : loadingStates.faceComparison ? (
                  <div className="spinner-small"></div>
                ) : faceCompareResults?.bestMatch?.matched ? '✓' : faceCompareResults?.frontMatch || faceCompareResults?.backMatch ? '✗' : '?'}
              </div>
              <div className="verification-content">
                <div className="verification-header-row">
                  <div>
                    <div className="verification-title">Face Compare</div>
                    <p className="verification-subtitle">
                      {livenessSkipped
                        ? 'Skipped - No selfie captured'
                        : loadingStates.faceComparison
                          ? 'Comparing faces...'
                          : faceCompareResults?.bestMatch?.matched
                            ? 'Selfie matches ID photo'
                            : faceCompareResults?.frontMatch || faceCompareResults?.backMatch
                              ? 'Face comparison complete'
                              : 'Awaiting comparison'}
                    </p>
                  </div>
                  {livenessSkipped ? (
                    <span className="confidence-badge skipped">SKIPPED</span>
                  ) : !faceCompareResults && !loadingStates.faceComparison && (
                    <button className="retry-button-small" onClick={retryFaceComparison}>
                      Run Comparison
                    </button>
                  )}
                </div>

                {faceCompareResults?.frontMatch && (
                  <div className="match-row">
                    <span className="match-label">Front ID Match:</span>
                    <span className={`match-percentage ${faceCompareResults.frontMatch.similarity >= config.verification.faceMatchThreshold ? 'success' : 'failed'}`}>
                      {faceCompareResults.frontMatch.similarity?.toFixed(1) || 'N/A'}%
                    </span>
                  </div>
                )}

                {faceCompareResults?.backMatch && (
                  <div className="match-row">
                    <span className="match-label">Back ID Match:</span>
                    <span className={`match-percentage ${faceCompareResults.backMatch.similarity >= config.verification.faceMatchThreshold ? 'success' : 'failed'}`}>
                      {faceCompareResults.backMatch.similarity?.toFixed(1) || 'N/A'}%
                    </span>
                  </div>
                )}

                {!livenessSkipped && !faceCompareResults?.frontMatch && !faceCompareResults?.backMatch && !loadingStates.faceComparison && !faceCompareLoading && (
                  <div className="match-row">
                    <span className="match-label">Status:</span>
                    <span className="match-percentage pending">Awaiting comparison</span>
                  </div>
                )}

                {!livenessSkipped && loadingStates.faceComparison && (
                  <div className="match-row">
                    <span className="match-label">Status:</span>
                    <span className="match-percentage loading">Analyzing...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Liveness Detection */}
            <div className={`verification-item ${livenessSkipped ? 'skipped' : loadingStates.liveness ? 'loading' : livenessResults?.status === 'SUCCEEDED' ? 'success' : 'failed'}`}>
              <div className="verification-icon">
                {livenessSkipped ? (
                  '−'
                ) : loadingStates.liveness ? (
                  <div className="spinner-small"></div>
                ) : livenessResults?.status === 'SUCCEEDED' ? '✓' : '✗'}
              </div>
              <div className="verification-content">
                <div className="verification-header-row">
                  <div>
                    <div className="verification-title">Liveness Detection</div>
                    <p className="verification-subtitle">
                      {livenessSkipped
                        ? 'Skipped - Face verification not performed'
                        : loadingStates.liveness
                          ? 'Analyzing liveness...'
                          : livenessResults?.status === 'SUCCEEDED'
                            ? 'Real person detected'
                            : 'Liveness check failed'}
                    </p>
                  </div>
                  {livenessSkipped ? (
                    <span className="confidence-badge skipped">SKIPPED</span>
                  ) : loadingStates.liveness ? (
                    <span className="confidence-badge loading">--</span>
                  ) : (
                    <span className={`confidence-badge ${livenessResults?.status === 'SUCCEEDED' ? 'success' : 'failed'}`}>
                      {livenessResults?.confidence?.toFixed(1) || '0'}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Signature Detection */}
      <div className="results-section">
        <h3>{TEXT_CONFIG.sectionSignature}</h3>

        {loadingStates.signatureDetection ? (
          <div className="section-loading">
            <div className="spinner-small"></div>
            <span>Detecting signatures...</span>
          </div>
        ) : (
          <div className={`verification-item ${signatureResults?.hasSignature ? 'success' : 'pending'}`}>
            <div className="verification-icon">
              {signatureResults?.hasSignature ? '✓' : '?'}
            </div>
            <div className="verification-content">
              <div className="verification-header-row">
                <div>
                  <div className="verification-title">Signature Presence</div>
                  <p className="verification-subtitle">
                    {signatureResults?.hasSignature
                      ? `${signatureResults.totalSignatures} signature(s) detected on ID`
                      : 'No signatures detected'}
                  </p>
                </div>
                <span className={`signature-count-badge ${signatureResults?.hasSignature ? '' : 'pending'}`}>
                  {signatureResults?.totalSignatures || 0} Signature{signatureResults?.totalSignatures !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Front Signature */}
              {signatureResults?.frontSignature?.detected && (
                <>
                  <div className="signature-detail-row">
                    <span className="signature-label">Front ID Signature:</span>
                    <span className="match-percentage success">
                      {signatureResults.frontSignature.confidence?.toFixed(1) || 'N/A'}%
                    </span>
                  </div>

                  {signatureResults.frontSignature.signatureBase64 && (
                    <div className="signature-image-box">
                      <div className="signature-image-header">
                        <span>🖼️ Front Signature</span>
                      </div>
                      <div className="signature-image-preview">
                        <img
                          src={`data:image/png;base64,${signatureResults.frontSignature.signatureBase64}`}
                          alt="Front Signature"
                        />
                      </div>
                      <div className="signature-confidence-text">
                        {signatureResults.frontSignature.confidence?.toFixed(1)}% confidence
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Back Signature */}
              {signatureResults?.backSignature?.detected && (
                <>
                  <div className="signature-detail-row">
                    <span className="signature-label">Back ID Signature:</span>
                    <span className="match-percentage success">
                      {signatureResults.backSignature.confidence?.toFixed(1) || 'N/A'}%
                    </span>
                  </div>

                  {signatureResults.backSignature.signatureBase64 && (
                    <div className="signature-image-box">
                      <div className="signature-image-header">
                        <span>🖼️ Back Signature</span>
                      </div>
                      <div className="signature-image-preview">
                        <img
                          src={`data:image/png;base64,${signatureResults.backSignature.signatureBase64}`}
                          alt="Back Signature"
                        />
                      </div>
                      <div className="signature-confidence-text">
                        {signatureResults.backSignature.confidence?.toFixed(1)}% confidence
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* No signatures detected */}
              {!signatureResults?.hasSignature && !isProcessing && (
                <div className="signature-detail-row">
                  <span className="signature-label">Status:</span>
                  <span className="match-percentage pending">No signatures found</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add to Digital Wallet */}
      <div className="results-section">
        <h3>{TEXT_CONFIG.sectionWallet}</h3>
        <p className="wallet-description">{TEXT_CONFIG.walletDescription}</p>

        {walletLoading ? (
          <div className="section-loading">
            <div className="spinner-small"></div>
            <span>Generating wallet passes...</span>
          </div>
        ) : walletError ? (
          <div className="wallet-error">
            <p>⚠️ Unable to generate wallet passes: {walletError}</p>
            <button
              className="retry-button"
              onClick={() => { setWalletError(null); createWalletPass(); }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="wallet-grid">
            <div className="wallet-item">
              <div className="wallet-header">
                <svg className="wallet-icon-svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <span>{TEXT_CONFIG.appleWallet}</span>
              </div>
              <div className="wallet-qr desktop-only">
                {walletData?.appleLink && (
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=${QR_CONFIG.size}x${QR_CONFIG.size}&data=${encodeURIComponent(walletData.appleLink)}`} alt="Apple Wallet QR" />
                )}
              </div>
              <a
                className="wallet-button"
                href={walletData?.appleLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
              >
                Add to {TEXT_CONFIG.appleWallet}
              </a>
            </div>

            <div className="wallet-item">
              <div className="wallet-header">
                <svg className="wallet-icon-svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
                </svg>
                <span>{TEXT_CONFIG.googleWallet}</span>
              </div>
              <div className="wallet-qr desktop-only">
                {walletData?.googleLink && (
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=${QR_CONFIG.size}x${QR_CONFIG.size}&data=${encodeURIComponent(walletData.googleLink)}`} alt="Google Wallet QR" />
                )}
              </div>
              <a
                className="wallet-button"
                href={walletData?.googleLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
              >
                Add to {TEXT_CONFIG.googleWallet}
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="results-actions">
        <button onClick={onReset} className="action-button primary">
          {TEXT_CONFIG.resetButton}
        </button>
      </div>
    </div>
  );
}

export default VerificationResults;
